import Localizer from 'localizer';
import PersonalRecordTypes from 'collections/prs/PersonalRecordTypes';
import PersonalRecord from 'models/prs/PersonalRecord';
import Personalizer from 'personalizer';
import ActivityTypeMapper from './ActivityTypeMapper';

var PersonalRecordTypeMapper = {
    /**
     * Function that returns a personalizer function for displaying a personal record
     * @param personalRecordTypeKey
     */
    getPersonalizerFunction: function(personalRecordTypeKey) {
        switch (personalRecordTypeKey) {
            case PersonalRecordTypes.Types.RUN_FARTHEST:
            case PersonalRecordTypes.Types.CYCLE_FARTHEST:
            case PersonalRecordTypes.Types.WHEELCHAIR_RUN_FARTHEST:
                return function(value) {
                    return (
                        Personalizer.personalizeDistance(value) +
                        ' ' +
                        Localizer.localize(Personalizer.getDistanceUnitKey())
                    );
                };
            case PersonalRecordTypes.Types.SWIM_LONGEST:
                return function(value, poolLengthUnit) {
                    var measurementSystem = null;

                    if (poolLengthUnit) {
                        // personalrecord model uses 'key' while activity model uses 'unitKey', so we gotta check both
                        measurementSystem =
                            poolLengthUnit.key === 'yard' || poolLengthUnit.unitKey === 'yard'
                                ? 'statute_us'
                                : 'metric';
                    } else {
                        measurementSystem = Personalizer.inferPoolMeasurementSystemFromDistance(value);
                    }

                    return (
                        Personalizer.personalizeSwimDistance(value, measurementSystem) +
                        ' ' +
                        Localizer.localize(Personalizer.getDistanceUnitKey('lap_swimming', value, poolLengthUnit))
                    );
                };
            case PersonalRecordTypes.Types.CYCLE_MAX_ELEV:
                return function(value) {
                    return (
                        Personalizer.personalizeElevation(value) +
                        ' ' +
                        Localizer.localize(Personalizer.getElevationUnitKey())
                    );
                };
            case PersonalRecordTypes.Types.CYCLE_MAX_POWER:
                return function(value) {
                    return (
                        Personalizer.personalizePower(value) + ' ' + Localizer.localize(Personalizer.getPowerUnitKey())
                    );
                };
            case PersonalRecordTypes.Types.RUN_1K:
            case PersonalRecordTypes.Types.RUN_5K:
            case PersonalRecordTypes.Types.RUN_10K:
            case PersonalRecordTypes.Types.RUN_HALF_MARATHON:
            case PersonalRecordTypes.Types.RUN_MARATHON:
            case PersonalRecordTypes.Types.RUN_1Mile:
            case PersonalRecordTypes.Types.CYCLE_40K:
            case PersonalRecordTypes.Types.SWIM_100M:
            case PersonalRecordTypes.Types.SWIM_400M:
            case PersonalRecordTypes.Types.SWIM_750M:
            case PersonalRecordTypes.Types.SWIM_1000M:
            case PersonalRecordTypes.Types.SWIM_1500M:
            case PersonalRecordTypes.Types.SWIM_100YD:
            case PersonalRecordTypes.Types.SWIM_500YD:
            case PersonalRecordTypes.Types.SWIM_1000YD:
            case PersonalRecordTypes.Types.SWIM_1650YD:
            case PersonalRecordTypes.Types.WHEELCHAIR_RUN_1K:
            case PersonalRecordTypes.Types.WHEELCHAIR_RUN_1Mile:
            case PersonalRecordTypes.Types.WHEELCHAIR_RUN_5K:
            case PersonalRecordTypes.Types.WHEELCHAIR_RUN_10K:
            case PersonalRecordTypes.Types.WHEELCHAIR_RUN_HALF_MARATHON:
            case PersonalRecordTypes.Types.WHEELCHAIR_RUN_MARATHON:
                return function(value) {
                    return Personalizer.personalizeDuration(value);
                };
            case PersonalRecordTypes.Types.STEPS_BEST_DAY:
            case PersonalRecordTypes.Types.STEPS_BEST_WEEK:
            case PersonalRecordTypes.Types.STEPS_BEST_MONTH:
            case PersonalRecordTypes.Types.PUSHES_BEST_DAY:
            case PersonalRecordTypes.Types.PUSHES_BEST_WEEK:
            case PersonalRecordTypes.Types.PUSHES_BEST_MONTH:
                return function(value) {
                    return Personalizer.personalizeSteps(value);
                };
            case PersonalRecordTypes.Types.STEPS_LONGEST_STREAK:
            case PersonalRecordTypes.Types.PUSHES_LONGEST_STREAK:
                return function(value) {
                    return value ? Localizer.localize('pr.label.steps.longest.streak.unit', value) : null;
                };
            case PersonalRecordTypes.Types.STEPS_CURRENT_STREAK:
            case PersonalRecordTypes.Types.PUSHES_CURRENT_STREAK:
                return function(value) {
                    return value ? Localizer.localize('pr.label.steps.current.streak.unit', value) : null;
                };
            case PersonalRecordTypes.Types.STRENGTH_TRAINING_BARBELL_BENCH_PRESS:
            case PersonalRecordTypes.Types.STRENGTH_TRAINING_BARBELL_OVERHEAD_PRESS:
            case PersonalRecordTypes.Types.STRENGTH_TRAINING_BARBELL_BACK_SQUAT:
            case PersonalRecordTypes.Types.STRENGTH_TRAINING_BARBELL_DEADLIFT:
            case PersonalRecordTypes.Types.STRENGTH_TRAINING_BARBELL_ROW:
                return function(value) {
                    return value
                        ? Personalizer.personalizeWeight(value) +
                              ' ' +
                              Localizer.localize(Personalizer.getWeightUnitKey())
                        : null;
                };
        }
    },

    /**
     * Function that maps a personal record's type to its sort order.
     * @param personalRecordType the Personal Record Type to get the sort key for.
     */
    getSortOrder: function(personalRecordType) {
        var sortOrder = PersonalRecordTypes.GetSortOrder(personalRecordType);
        if (typeof sortOrder == 'undefined') {
            if (console) {
                console.log('Unrecognized Personal Record Type: ' + personalRecordType);
            }
            sortOrder = 99;
        }

        return sortOrder;
    },

    /**
     * Given a personal record type key, determines if the record type is cycling swimming or running.
     */
    getRecordTypeFromKey: function(typeKey) {
        // second one is to address typo in database
        var Types = PersonalRecordTypes.Types;
        switch (typeKey) {
            case Types.CYCLE_40K:
            case Types.CYCLE_MAX_ELEV:
            case Types.CYCLE_FARTHEST:
            case Types.CYCLE_MAX_POWER:
                return PersonalRecord.Sports.Cycle;
                break;
            case Types.RUN_1K:
            case Types.RUN_5K:
            case Types.RUN_10K:
            case Types.RUN_HALF_MARATHON:
            case Types.RUN_MARATHON:
            case Types.RUN_FARTHEST:
            case Types.RUN_1Mile:
                return PersonalRecord.Sports.Run;
                break;
            case Types.STEPS_BEST_DAY:
            case Types.STEPS_BEST_WEEK:
            case Types.STEPS_BEST_MONTH:
            case Types.STEPS_LONGEST_STREAK:
            case Types.STEPS_CURRENT_STREAK:
                return PersonalRecord.Sports.Steps;
                break;
            case Types.SWIM_100M:
            case Types.SWIM_100YD:
            case Types.SWIM_400M:
            case Types.SWIM_500YD:
            case Types.SWIM_750M:
            case Types.SWIM_1000M:
            case Types.SWIM_1000YD:
            case Types.SWIM_1500M:
            case Types.SWIM_1650YD:
            case Types.SWIM_LONGEST:
                return PersonalRecord.Sports.Swim;
                break;
            case Types.STRENGTH_TRAINING_BARBELL_BENCH_PRESS:
            case Types.STRENGTH_TRAINING_BARBELL_OVERHEAD_PRESS:
            case Types.STRENGTH_TRAINING_BARBELL_BACK_SQUAT:
            case Types.STRENGTH_TRAINING_BARBELL_DEADLIFT:
            case Types.STRENGTH_TRAINING_BARBELL_ROW:
                return PersonalRecord.Sports.Training;

            case Types.PUSHES_BEST_DAY:
            case Types.PUSHES_BEST_WEEK:
            case Types.PUSHES_BEST_MONTH:
            case Types.PUSHES_LONGEST_STREAK:
            case Types.PUSHES_CURRENT_STREAK:
                return PersonalRecord.Sports.Pushes;

            case Types.WHEELCHAIR_RUN_1K:
            case Types.WHEELCHAIR_RUN_5K:
            case Types.WHEELCHAIR_RUN_10K:
            case Types.WHEELCHAIR_RUN_HALF_MARATHON:
            case Types.WHEELCHAIR_RUN_MARATHON:
            case Types.WHEELCHAIR_RUN_FARTHEST:
            case Types.WHEELCHAIR_RUN_1Mile:
                return PersonalRecord.Sports.Parasports;
        }

        return null;
    },

    getActivityTypeKeyByPRSport: function(sportType) {
        switch (sportType.toLowerCase()) {
            case 'running':
                return ActivityTypeMapper.Running;
            case 'cycling':
                return ActivityTypeMapper.Cycling;
            case 'swimming':
                return ActivityTypeMapper.LapSwimming;
            case PersonalRecord.Sports.Training.toLowerCase():
                return ActivityTypeMapper.StrengthTraining;
            default:
                return null;
        }
    },

    getStrengthPersonalRecordExercises: function() {
        return [
            {
                categoryKey: 'BENCH_PRESS',
                exerciseKey: 'BARBELL_BENCH_PRESS',
                prKey: PersonalRecordTypes.Types.STRENGTH_TRAINING_BARBELL_BENCH_PRESS,
                prTypeId: 28
            },
            {
                categoryKey: 'SHOULDER_PRESS',
                exerciseKey: 'OVERHEAD_BARBELL_PRESS',
                prKey: PersonalRecordTypes.Types.STRENGTH_TRAINING_BARBELL_OVERHEAD_PRESS,
                prTypeId: 29
            },
            {
                categoryKey: 'SQUAT',
                exerciseKey: 'BARBELL_BACK_SQUAT',
                prKey: PersonalRecordTypes.Types.STRENGTH_TRAINING_BARBELL_BACK_SQUAT,
                prTypeId: 30
            },
            {
                categoryKey: 'DEADLIFT',
                exerciseKey: 'BARBELL_DEADLIFT',
                prKey: PersonalRecordTypes.Types.STRENGTH_TRAINING_BARBELL_DEADLIFT,
                prTypeId: 31
            },
            {
                categoryKey: 'ROW',
                exerciseKey: 'BARBELL_ROW',
                prKey: PersonalRecordTypes.Types.STRENGTH_TRAINING_BARBELL_ROW,
                prTypeId: 32
            }
        ];
    },

    getExerciseByPRKey: function(prKey) {
        const exercises = this.getStrengthPersonalRecordExercises();
        return exercises.find((exercise) => exercise.prKey === prKey);
    },

    getExerciseByPRTypeId: function(prTypeId) {
        const exercises = this.getStrengthPersonalRecordExercises();
        const id = typeof prTypeId === 'string' ? parseInt(prTypeId) : prTypeId;
        return exercises.find((exercise) => exercise.prTypeId === id);
    },

    getTypeKeyNamespace: function(typeKey) {
        var Types = PersonalRecordTypes.Types;
        switch (typeKey) {
            case Types.CYCLE_40K:
            case Types.CYCLE_MAX_ELEV:
            case Types.CYCLE_FARTHEST:
            case Types.CYCLE_MAX_POWER:
            case Types.RUN_1K:
            case Types.RUN_5K:
            case Types.RUN_10K:
            case Types.RUN_HALF_MARATHON:
            case Types.RUN_MARATHON:
            case Types.RUN_FARTHEST:
            case Types.RUN_1Mile:
            case Types.STEPS_BEST_DAY:
            case Types.STEPS_BEST_WEEK:
            case Types.STEPS_BEST_MONTH:
            case Types.STEPS_LONGEST_STREAK:
            case Types.STEPS_CURRENT_STREAK:
            case Types.PUSHES_BEST_DAY:
            case Types.PUSHES_BEST_WEEK:
            case Types.PUSHES_BEST_MONTH:
            case Types.PUSHES_LONGEST_STREAK:
            case Types.PUSHES_CURRENT_STREAK:
            case Types.WHEELCHAIR_RUN_1K:
            case Types.WHEELCHAIR_RUN_5K:
            case Types.WHEELCHAIR_RUN_10K:
            case Types.WHEELCHAIR_RUN_HALF_MARATHON:
            case Types.WHEELCHAIR_RUN_MARATHON:
            case Types.WHEELCHAIR_RUN_FARTHEST:
            case Types.WHEELCHAIR_RUN_1Mile:
                return 'userprofile';
                break;
            case Types.SWIM_100M:
            case Types.SWIM_100YD:
            case Types.SWIM_400M:
            case Types.SWIM_500YD:
            case Types.SWIM_750M:
            case Types.SWIM_1000M:
            case Types.SWIM_1000YD:
            case Types.SWIM_1500M:
            case Types.SWIM_1650YD:
            case Types.SWIM_LONGEST:
            case Types.STRENGTH_TRAINING_BARBELL_BENCH_PRESS:
            case Types.STRENGTH_TRAINING_BARBELL_OVERHEAD_PRESS:
            case Types.STRENGTH_TRAINING_BARBELL_BACK_SQUAT:
            case Types.STRENGTH_TRAINING_BARBELL_DEADLIFT:
            case Types.STRENGTH_TRAINING_BARBELL_ROW:
                return 'personal-record-api';
                break;
        }

        return '';
    }
};

export default PersonalRecordTypeMapper;
